import * as React from "react";
import { connect } from "react-redux";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import TextField from "@material-ui/core/TextField";
import { StoreState } from "../../reducers/root-reducer";
import { Area } from "../../api/areas";

import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";

export interface AddTableDialogData {
	id: string;
	key: string;
	areaId: string;
	displayName: string;
	peopleSoftLimit: number;
}

interface State extends AddTableDialogData {
	index: number;
	isEditing: boolean;
}

export interface AddTableDialogProps {
	open: boolean;
	id: string;
	index: number;
	itemKey?: string;
	areaId?: string;
	displayName?: string;
	peopleSoftLimit?: number;
	onClose: () => void;
	onSubmit: (
		data: AddTableDialogData,
		isEditing: boolean,
		index: number
	) => void;
}

interface ConnectedAddTableDialogProps extends WithStyles<typeof styles> {
	areas: Area[];
}

type Props = AddTableDialogProps &
	ConnectedAddTableDialogProps &
	InjectedTranslateProps;

const initialState: State = {
	isEditing: false,
	index: null,
	id: null,
	key: "",
	areaId: "",
	displayName: "",
	peopleSoftLimit: 0
};

export class AddTableDialog extends React.Component<Props, State> {
	public state: State = { ...initialState };

	componentWillReceiveProps(nextProps: Props) {
		const {
			id,
			index,
			itemKey,
			areaId,
			displayName,
			peopleSoftLimit,
			areas
		} = nextProps;

		if (itemKey || displayName) {
			this.setState({
				isEditing: true,
				id: id || null,
				index: index || null,
				key: itemKey || "",
				areaId: areaId || areas[0].id || "",
				displayName: displayName || "",
				peopleSoftLimit: peopleSoftLimit || 0
			});
		}
	}

	handleKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ key: event.target.value });
	};

	handleDisplayNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ displayName: event.target.value });
	};

	handlePeopleSoftLimitChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		this.setState({ peopleSoftLimit: Number(event.target.value) });
	};

	handleAreaChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		this.setState({ areaId: event.target.value });
	};

	handleCancelClick = () => {
		this.setState(initialState, () => {
			this.props.onClose();
		});
	};

	handleFormSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		const { id, key, areaId, displayName, peopleSoftLimit } = this.state;

		this.props.onSubmit(
			{ id, key, areaId, displayName, peopleSoftLimit },
			this.state.isEditing,
			this.props.index
		);
		this.setState(initialState);
	};

	render() {
		const { classes, open, onClose, areas } = this.props;

		return (
			<Dialog open={open} onClose={onClose}>
				<DialogTitle>Tisch hinzufügen</DialogTitle>
				<DialogContent>
					<form onSubmit={this.handleFormSubmit}>
						<TextField
							className={classes.keyInput}
							value={this.state.key}
							onChange={this.handleKeyChange}
							label="Key"
							margin="normal"
							fullWidth
							required
							inputProps={{
								disabled: this.state.isEditing,
								pattern: "[a-z0-9]*",
								title: "Alphanumerisch ohne Großschreibung"
							}}
						/>
						<FormControl className={classes.areaSelect} fullWidth>
							<InputLabel htmlFor="age-simple">
								Bereich
							</InputLabel>
							<Select
								value={this.state.areaId}
								onChange={this.handleAreaChange}
								fullWidth
							>
								<MenuItem value="">
									<em>Bitte auswählen!</em>
								</MenuItem>
								{areas.map(area => (
									<MenuItem key={area.id} value={area.id}>
										{area.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							className={classes.displayNameInput}
							value={this.state.displayName}
							onChange={this.handleDisplayNameChange}
							label="Anzeigename"
							margin="normal"
							fullWidth
							required
						/>
						<TextField
							className={classes.peopleSoftLimitInput}
							value={this.state.peopleSoftLimit}
							onChange={this.handlePeopleSoftLimitChange}
							label="Personen Soft-Limit"
							margin="normal"
							fullWidth
							required
							inputProps={{ type: "number" }}
						/>
						<div className={classes.buttonContainer}>
							<Button
								className={classes.cancelButton}
								onClick={this.handleCancelClick}
							>
								Abbrechen
							</Button>
							<Button
								className={classes.submitButton}
								type="submit"
								color="primary"
							>
								Hinzufügen
							</Button>
						</div>
					</form>
				</DialogContent>
			</Dialog>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	areas: state.areas
});

const styles = (theme: Theme) =>
	createStyles({
		root: {
			cursor: "move"
		},
		buttonContainer: {
			display: "flex",
			justifyContent: "flex-end",
			marginTop: 25
		},
		keyInput: {},
		areaSelect: {},
		displayNameInput: {},
		peopleSoftLimitInput: {},
		cancelButton: {},
		submitButton: {}
	});

export const ConnectedAddTableDialog = translate("translations")(
	withStyles(styles)(connect(mapStateToProps)(AddTableDialog))
);
